/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from 'react';
import useScript from 'react-script-hook';
import { AxRoute } from 'src/features/DUP';
import { configVars } from 'src/helpers';
import { useRequest, useResource } from 'src/hooks/useResource';
import { useTelemetry } from 'src/hooks/useTelemetry';
import { Account, ArgyleUser } from 'src/types/applicantExperienceApi';

export type PayrollLinking = ReturnType<typeof usePayrollLinking>;

interface UserData {
  // This is true if the user connects at least one account. Otherwise, false.
  userSubmissionComplete: boolean;
}

interface LinkConfig {
  flowId: string;
  sandbox: boolean;
  userToken: string;
  onClose: (data: UserData) => void;
}

declare global {
  interface Window {
    Argyle: any;
  }
}

const DEFAULT_ARGYLE_LINK_URL = 'https://plugin.argyle.com/argyle.web.v5.js';

export const usePayrollLinking = (argyleUser?: ArgyleUser) => {
  const { makeRequest } = useRequest();
  // To do: Second argument after loading is an error that needs to be handled.
  const [isLoadingWidget] = useScript({ src: DEFAULT_ARGYLE_LINK_URL, checkForExisting: true });
  const [argyleInstance, setArgyleInstance]: any = useState(null);
  const [argyleConnectActive, setArgyleConnectActive] = useState<boolean>(false);
  const [isDisconnectingAccount, setIsDisconnectingAccount] = useState<boolean>(false);

  const [accountsConnected, { refresh, applyLocalUpdates }] = useResource<
    Account[] | { error: string }
  >(argyleUser ? `/${AxRoute.CONNECTED_PAYROLL}/accounts_connected` : '');

  const { trackAccountsConnected } = useTelemetry();

  const resetArgyle = useCallback(
    (data: UserData) => {
      setArgyleConnectActive(false);
      refresh();
    },
    [refresh]
  );

  const linkConfig: LinkConfig | undefined = useMemo(
    () =>
      argyleUser && {
        flowId: configVars.argyle.flow_id,
        sandbox: configVars.env !== 'production',
        userToken: argyleUser.userToken,
        onClose: resetArgyle
      },
    [argyleUser, resetArgyle]
  );

  useEffect(() => {
    if (!isLoadingWidget && !argyleInstance && linkConfig) {
      const instance = window.Argyle.create(linkConfig);
      setArgyleInstance(instance);
    }
  }, [isLoadingWidget, argyleInstance, linkConfig]);

  useEffect(() => {
    if (argyleInstance && argyleConnectActive) {
      argyleInstance.open();
    }
  }, [argyleInstance, argyleConnectActive]);

  useEffect(() => {
    if (Array.isArray(accountsConnected) && accountsConnected.length > 0) {
      trackAccountsConnected(accountsConnected);
    }
  }, [accountsConnected, trackAccountsConnected]);

  const disconnectAccount = useCallback(
    async (accountId: string) => {
      if (Array.isArray(accountsConnected) && accountsConnected.length > 0) {
        setIsDisconnectingAccount(true);
        const response = await makeRequest<string>({
          path: `/${AxRoute.CONNECTED_PAYROLL}/account/${accountId}`,
          method: 'DELETE'
        });

        if (response) {
          applyLocalUpdates(accountsConnected.filter((account) => account.accountId !== accountId));
        }
      }
      setIsDisconnectingAccount(false);
    },
    [accountsConnected, makeRequest, applyLocalUpdates]
  );

  return {
    openWidget: () => setArgyleConnectActive(true),
    accountsConnected: Array.isArray(accountsConnected) ? accountsConnected : [],
    isLoadingAccounts: argyleUser ? accountsConnected === undefined : false,
    isDisconnectingAccount,
    isLoadingWidget,
    disconnectAccount
  };
};
